import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, Paperclip } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { Blaettern, type PositionsNavigation } from '@/components/positionswahl/PositionsWahl'
import { api } from '@/lib/axios'
import { formCard } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { Abschnitt, FeldZeile, feldKlasse, mitStand, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { fetchUnits } from '../api/calculation-positions.api'
import type { CalcGap, CalcPosition, CalcUnit, Calculation } from '../types/calculation.types'
import { KalkulationsBlatt } from './KalkulationsBlatt'
import { KalkulationsDateien, dateienHinweis, haengeAn } from './KalkulationsDateien'
import { NICHT_ANGEBOTEN, ZUSTAND_ANZEIGE } from './PositionsZeile'

const ARTEN: { wert: CalcPosition['kind']; text: string }[] = [
  { wert: 'NORMAL', text: 'Normalposition' },
  { wert: 'OPTIONAL', text: 'Eventualposition (zählt nicht)' },
  { wert: 'ALTERNATIVE', text: 'Wahlposition (zählt nicht)' },
  { wert: 'REGIE', text: 'Regiestunden' },
  { wert: 'SURCHARGE', text: 'Zuschlagsposition' },
  { wert: 'TEXT', text: 'Textposition (ohne Preis)' },
]

/**
 * AP-37 9. — **eine Position**: Kopf wie im Projektmanagement (AP-35 Blatt 2
 * 8.2: eine Zeile „Menge Einheit Bezeichnung", Zustand daneben, EP rechts)
 * und Reiter Blatt · Texte. Seit Nachtrag 8 nimmt sie die ganze Breite des Reiters;
 * die Positionsliste klappt an ihrer Überschrift auf (`navigation`, N8-2/N8-3).
 *
 * Das Blatt (Material, Arbeitsschritte, Ergebnisspalte) ist `KalkulationsBlatt`;
 * es teilt die Speicherstelle der Position für den EP von Hand.
 *
 * Nachtrag 3 (Detailblatt O 4a): Reiter **Dateien** — Unterlagen an der Position
 * und ihren Materialzeilen. Die Büroklammer im Kopf springt dorthin, und Dateien
 * lassen sich auf den Kopf ziehen.
 */
export type PositionsAnsicht = 'blatt' | 'texte' | 'dateien'

export function PositionAnsicht({
  kalkulation,
  positionId,
  onGeaendert,
  ansicht,
  onAnsicht,
  navigation,
}: {
  kalkulation: Calculation
  positionId: string
  onGeaendert: () => void
  /** N1-21: Blatt oder Texte — vom Aufrufer gehalten, damit die Wahl beim Wechsel der Position bleibt. */
  ansicht?: PositionsAnsicht
  onAnsicht?: (a: PositionsAnsicht) => void
  /** Nachtrag 8: Blättern und Aufklappliste der Positionen — gezeichnet im Kopf. */
  navigation?: PositionsNavigation
}) {
  const { user } = useAuthStore()
  const darf = kalkulation.writable && erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const [p, setP] = useState<CalcPosition | null>(null)
  const [dateiFehler, setDateiFehler] = useState<string | null>(null)
  const [einheiten, setEinheiten] = useState<CalcUnit[]>([])
  const [eigene, setEigene] = useState<PositionsAnsicht>('blatt')
  const reiter = ansicht ?? eigene
  const setReiter = onAnsicht ?? setEigene
  const [fehler, setFehler] = useState<string | null>(null)
  const url = `/calculations/${kalkulation.id}/positions/${positionId}`

  const laden = useCallback(() => {
    api
      .get<{ data: CalcPosition }>(url)
      .then((r) => setP(r.data.data))
      .catch((e) => setFehler(apiErrorText(e, 'Die Position ließ sich nicht laden.')))
  }, [url])
  useEffect(() => {
    laden()
    fetchUnits().then(setEinheiten).catch(() => setEinheiten([]))
  }, [laden])

  // Detailblatt K 3.3: Jede Lücke ist ein eigenes Feld `luecke_<Nr>` derselben Speicherstelle.
  const daten = useMemo(() => (p ? mitLueckenfeldern(p) : null), [p])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `calcPosition:${positionId}`,
    bezeichnung: p ? `Position ${p.displayNumber}` : 'Position',
    daten: daten as unknown as Datensatz | null,
    schreibe: (patch, stand) =>
      api.patch<{ data: CalcPosition }>(url, lueckenAlsAntworten(patch), mitStand(stand)).then((r) => {
        laden()
        onGeaendert()
        return { ...r.data, data: mitLueckenfeldern(r.data.data) } as unknown as Datensatz
      }),
    keepalive: { url, method: 'PATCH' },
  }) as Speicherstelle<unknown>

  if (!p) return <p className="text-sm text-muted-foreground">{fehler ?? 'Lädt …'}</p>
  // Eine Textposition hat kein Blatt — Texte und Dateien schon.
  const sicht: PositionsAnsicht = p.kind === 'TEXT' && reiter === 'blatt' ? 'texte' : reiter
  const neu = () => {
    laden()
    onGeaendert()
  }
  const aufDenKopf = async (dateien: File[]) => {
    setDateiFehler(null)
    const r = await haengeAn(kalkulation.id, dateien, { positionId: p.id })
    if (r.fehler.length) setDateiFehler(r.fehler.join(' · '))
    if (r.ok) {
      setReiter('dateien')
      neu()
    }
  }

  return (
    <div className="space-y-4">
      <PositionsKopf
        p={p}
        stelle={stelle}
        einheiten={einheiten}
        darf={darf}
        navigation={navigation}
        onDateien={() => setReiter('dateien')}
        onDateienAblegen={darf ? (d) => void aufDenKopf(d) : undefined}
      />
      {dateiFehler && <p className="text-sm text-destructive">{dateiFehler}</p>}

      {/* N1-22: Eine Textposition hat kein Blatt — sie zeigt gleich die Texte; die Wahl für die übrigen bleibt. */}
      <div className="flex flex-wrap items-center gap-1 border-b border-border">
        {(
          [
            ['blatt', 'Blatt'],
            ['texte', 'Texte'],
            ['dateien', p.fileCount ? `Dateien (${p.fileCount})` : 'Dateien'],
          ] as const
        )
          .filter(([k]) => k !== 'blatt' || p.kind !== 'TEXT')
          .map(([k, t]) => (
            <button
              key={k}
              type="button"
              onClick={() => setReiter(k)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm ${sicht === k ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              {t}
            </button>
          ))}
      </div>

      {sicht === 'blatt' && <KalkulationsBlatt kalkulation={kalkulation} position={p} stelle={stelle} einheiten={einheiten} onGeaendert={neu} />}
      {sicht === 'texte' && <TexteKarte p={p} stelle={stelle} darf={darf} />}
      {sicht === 'dateien' && (
        <div className={formCard}>
          <KalkulationsDateien kalkulationId={kalkulation.id} ziel={{ positionId: p.id }} darf={darf} onGeaendert={neu} />
        </div>
      )}
    </div>
  )
}

const mengeText = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 3 })
const einheitCode = (p: CalcPosition, einheiten: CalcUnit[]) => einheiten.find((e) => e.id === p.unitId)?.code ?? p.unitCode ?? p.unitRaw ?? ''

/** Art der Position im Kopf — nur, wenn sie nicht normal ist. */
const ART_KURZ: Partial<Record<CalcPosition['kind'], string>> = {
  OPTIONAL: 'Eventualposition',
  ALTERNATIVE: 'Wahlposition',
  REGIE: 'Regie',
  SURCHARGE: 'Zuschlag',
  TEXT: 'Textposition',
}

/**
 * Kopf der Position wie im Projektmanagement (AP-35 Blatt 2 8.2, Nachtrag 7):
 * eine kompakte Karte, oben klein Nummer bzw. OZ, Art und LV-Hinweis, darunter
 * **eine** Zeile in der Reihenfolge, in der der Betrieb eine Position nennt —
 * Menge, Einheit, Bezeichnung („12 lfm Geländer gerade"). Gesperrt (aus dem LV,
 * ohne Recht, festgeschrieben) lesen sich die Felder wie die Kennzeile im
 * Projekt. Rechts EP und GP nebeneinander, der Zustand ganz rechts (N1-16).
 *
 * Nachtrag 8: links davor ‹ › mit Zähler (N8-2), die Bezeichnung größer (N8-5), und an ihr klappt die
 * Positionsliste auf (N8-3) — gesperrt ist die ganze Überschrift der Knopf; bearbeitbar bleibt sie ein
 * Eingabefeld, und der Pfeil ganz rechts darin öffnet die Liste.
 */
function PositionsKopf({
  p,
  stelle,
  einheiten,
  darf,
  navigation,
  onDateien,
  onDateienAblegen,
}: {
  p: CalcPosition
  stelle: Speicherstelle<unknown>
  einheiten: CalcUnit[]
  darf: boolean
  navigation?: PositionsNavigation
  /** Büroklammer: zum Reiter Dateien. */
  onDateien: () => void
  /** Dateien aus dem Explorer auf den Kopf ziehen (4a.2). */
  onDateienAblegen?: (dateien: File[]) => void
}) {
  const gesperrt = !darf || p.isLvLocked
  const text = useFeld<string>(stelle, 'shortText', { label: 'Bezeichnung', pflicht: true, gesperrt })
  const menge = useFeld<number | null>(stelle, 'quantity', { art: 'zahl', label: 'Menge', stellen: 3, gesperrt: gesperrt || p.kind === 'TEXT' })
  const einheit = useFeld<string | null>(stelle, 'unitId', { art: 'auswahl', label: 'Einheit', gesperrt })
  // Detailblatt K 3.4: nur an LV-Positionen, nicht an Textpositionen.
  const nichtAngeboten = useFeld<boolean>(stelle, 'notOffered', { art: 'schalter', label: 'nicht angeboten', gesperrt: !darf })
  const z = p.notOffered ? NICHT_ANGEBOTEN : ZUSTAND_ANZEIGE[p.state]
  const art = ART_KURZ[p.kind]
  const [ziel, setZiel] = useState(false)
  return (
    <div
      className={`${formCard} !px-4 !py-2 ${ziel ? 'ring-2 ring-primary' : ''}`}
      onDragOver={(e) => {
        if (!onDateienAblegen || !e.dataTransfer.types.includes('Files')) return
        e.preventDefault()
        setZiel(true)
      }}
      onDragLeave={() => setZiel(false)}
      onDrop={(e) => {
        setZiel(false)
        if (!onDateienAblegen || !e.dataTransfer.files.length) return
        e.preventDefault()
        onDateienAblegen(Array.from(e.dataTransfer.files))
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <div className="flex min-w-0 flex-1 items-end gap-3">
        {navigation && (
          <div className={gesperrt ? 'mb-1' : 'mb-2'}>
            <Blaettern stelle={navigation.stelle} anzahl={navigation.anzahl} onSchritt={navigation.schritt} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-3 text-xs leading-none text-muted-foreground">
            <p className="min-w-0 flex-1 truncate">
              <span className="font-mono">{p.oz ? `OZ ${p.displayNumber}` : `Pos. ${p.displayNumber}`}</span>
              {art && <span> · {art}</span>}
              {p.isLvLocked && <span> · aus dem LV — Texte, Menge und Einheit sind gesperrt, ausgepreist wird nur</span>}
            </p>
            {/* Detailblatt K 3.4: außerhalb der gekürzten Zeile, damit der Schalter immer sichtbar bleibt. */}
            {p.isLvLocked && p.kind !== 'TEXT' && (
              <label className="inline-flex shrink-0 items-center gap-1" title="Die Position wird ohne Preis abgegeben und zählt in keiner Summe.">
                <input type="checkbox" className="h-3 w-3" {...nichtAngeboten.schalter} /> nicht angeboten
              </label>
            )}
          </div>
          <div className="mt-1.5 flex min-w-0 items-center gap-2">
            {gesperrt ? (
              // Gesperrt liest sich der Kopf wie die Kennzeile im Projekt: „0 m² Ziegeldecke …".
              <Ueberschrift navigation={navigation}>
                <h2 className="min-w-0 truncate text-[22px] font-semibold leading-7" title={p.shortText}>
                  {p.kind !== 'TEXT' && (
                    <span className={`font-normal ${p.unitRaw && !p.unitId ? 'text-amber-700 dark:text-amber-400' : ''}`}>
                      {mengeText(p.quantity)} {einheitCode(p, einheiten)}{' '}
                    </span>
                  )}
                  {p.shortText}
                </h2>
              </Ueberschrift>
            ) : (
              <>
                {p.kind !== 'TEXT' && (
                  <>
                    <input
                      className={`${feldKlasse} !w-24 shrink-0 text-right !text-xl tabular-nums`}
                      inputMode="decimal"
                      aria-label="Menge"
                      title="Menge"
                      placeholder="Menge"
                      {...menge.props}
                    />
                    {p.unitRaw && !p.unitId ? (
                      <span className="w-20 shrink-0 text-amber-700 dark:text-amber-400" title="Einheit aus dem LV ohne Übersetzung — im Reiter LV übersetzen">
                        {p.unitRaw}
                      </span>
                    ) : (
                      <select className={`${feldKlasse} !w-24 shrink-0 !text-xl`} aria-label="Einheit" title="Einheit" {...einheit.props}>
                        <option value="">—</option>
                        {einheiten.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.code}
                          </option>
                        ))}
                      </select>
                    )}
                  </>
                )}
                <div className="relative min-w-0 flex-1">
                  <input
                    className={`${feldKlasse} !text-xl font-semibold ${navigation ? '!pr-11' : ''}`}
                    aria-label="Bezeichnung"
                    title={p.shortText}
                    placeholder="Bezeichnung"
                    {...text.props}
                  />
                  {navigation && (
                    // N8-3: Die Bezeichnung bleibt ein Eingabefeld — die Liste öffnet der Pfeil ganz rechts darin.
                    <button
                      type="button"
                      className="absolute inset-y-px right-px flex w-9 items-center justify-center rounded-r-[calc(var(--radius)-3px)] border-l border-border text-muted-foreground hover:bg-primary/15 hover:text-primary aria-expanded:bg-primary/15 aria-expanded:text-primary"
                      aria-expanded={navigation.offen}
                      aria-label="Positionen zeigen"
                      title="Positionen zeigen (Alt + ↓)"
                      onClick={() => navigation.setOffen(!navigation.offen)}
                    >
                      <ChevronDown className="h-[18px] w-[18px]" />
                    </button>
                  )}
                  {navigation?.offen && navigation.fenster}
                </div>
              </>
            )}
          </div>
        </div>
        </div>
        {/* N1-16: EP und GP nebeneinander auf der Höhe der Eingabezeile, der Zustand ganz rechts. */}
        <div className="flex shrink-0 items-end gap-5 self-end">
          <Betrag titel={`EP${p.fixedUnitPrice != null ? ' (von Hand)' : ''}`} wert={p.kind === 'TEXT' || p.notOffered ? '—' : geldText(p.unitPrice)} />
          <Betrag titel="GP" wert={p.kind === 'TEXT' || p.notOffered ? '—' : geldText(p.totalPrice)} />
          {/* Eine Textposition hat keinen Preis — also auch keinen Zustand „ausgepreist“. */}
          {p.kind !== 'TEXT' && <span className={`mb-1 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${z.klasse}`}>{z.label}</span>}
          {/* 4a.2: Büroklammer neben dem Zustand — zählt Position und Materialzeilen zusammen. */}
          <button
            type="button"
            onClick={onDateien}
            className="mb-1 inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            title={p.fileCount ? dateienHinweis(p.fileCount, p.fileCountLines) : 'Dateien der Position — auch hierher ziehen'}
          >
            <Paperclip className="h-4 w-4" />
            {p.fileCount > 0 && <span className="tabular-nums">{p.fileCount}</span>}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Die gesperrte Überschrift: mit Navigation ein Knopf, der die Positionsliste aufklappt (Pfeil ganz rechts).
 * Nachtrag 9 (Auftraggeber 13.10.2026): nach rechts breiter — zwischen Text und Pfeil bleibt Luft, und eine kurze
 * Bezeichnung macht den Rahmen nicht schmal.
 */
function Ueberschrift({ navigation, children }: { navigation?: PositionsNavigation; children: ReactNode }) {
  if (!navigation) return <div className="min-w-0 flex-1">{children}</div>
  return (
    <div className="relative flex min-w-0">
      <button
        type="button"
        className="inline-flex min-w-[14rem] max-w-full items-center justify-between gap-8 rounded-md border border-border/70 bg-background py-0.5 pl-3 pr-2.5 text-left hover:border-primary aria-expanded:border-primary"
        aria-expanded={navigation.offen}
        title="Positionen zeigen (Alt + ↓)"
        onClick={() => navigation.setOffen(!navigation.offen)}
      >
        {children}
        <ChevronDown className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />
      </button>
      {navigation.offen && navigation.fenster}
    </div>
  )
}

function Betrag({ titel, wert }: { titel: string; wert: string }) {
  return (
    <div className="text-right">
      <span className="block text-[11px] leading-none text-muted-foreground">{titel}</span>
      <span className="block text-xl font-semibold leading-tight tabular-nums">{wert}</span>
    </div>
  )
}

/**
 * N1-22: ein Textfeld, das mit seinem Inhalt wächst — LV-Texte sind oft lang und
 * sollen ganz lesbar sein, ohne im Feld zu scrollen. Die `ref` von `useFeld` bleibt erhalten.
 */
function WachsendesFeld({ feld, zeilen }: { feld: ReturnType<typeof useFeld>['props']; zeilen: number }) {
  const eigen = useRef<HTMLTextAreaElement | null>(null)
  const { ref: feldRef, ...rest } = feld
  useLayoutEffect(() => {
    const el = eigen.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + 2}px`
  }, [rest.value])
  return (
    <textarea
      {...rest}
      ref={(el) => {
        eigen.current = el
        feldRef(el)
      }}
      rows={zeilen}
      className={`${feldKlasse} resize-y overflow-hidden text-[15px] leading-relaxed`}
    />
  )
}

const LUECKE = 'luecke_'

/** Die Antworten der Lücken als Felder der Position, damit `useFeld` sie wie jedes andere Feld führt. */
function mitLueckenfeldern(p: CalcPosition) {
  return { ...p, ...Object.fromEntries((p.gaps ?? []).map((l) => [`${LUECKE}${l.seq}`, l.answer])) }
}

/** Der Rückweg: `luecke_<Nr>` → `gapAnswers` des PATCH (Detailblatt K 9.). */
function lueckenAlsAntworten(patch: Record<string, unknown>) {
  const antworten: Record<string, unknown> = {}
  const rest: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(patch)) {
    if (k.startsWith(LUECKE)) antworten[k.slice(LUECKE.length)] = v === '' ? null : v
    else rest[k] = v
  }
  return Object.keys(antworten).length ? { ...rest, gapAnswers: antworten } : rest
}

const LUECKEN_ART: Record<CalcGap['kind'], string> = {
  REQUIRED: 'Bieterlücke',
  OPTIONAL: 'Bieterlücke, freiwillig',
  KEYWORD: 'Lücke im Stichwort',
}

/** Eine Lücke: der Text davor als Beschriftung, der Platzhalter der Ausschreibung im leeren Feld. */
function LueckenFeld({ l, stelle, darf }: { l: CalcGap; stelle: Speicherstelle<unknown>; darf: boolean }) {
  const feld = useFeld<string | null>(stelle, `${LUECKE}${l.seq}`, {
    label: LUECKEN_ART[l.kind],
    gesperrt: !darf,
    pruefe: (w) => (l.maxLength && typeof w === 'string' && w.trim().length > l.maxLength ? `Höchstens ${l.maxLength} Zeichen in dieser Lücke.` : null),
  })
  return (
    <FeldZeile label={`${LUECKEN_ART[l.kind]}${l.code ? ` ${l.code}` : ''}`} status={feld} hilfe={l.textBefore ? `… ${l.textBefore} ___` : undefined}>
      <input
        className={`${feldKlasse} ${l.kind === 'OPTIONAL' ? '' : 'font-medium'}`}
        placeholder={l.placeholder ?? (l.kind === 'OPTIONAL' ? 'freiwillig' : 'vom Bieter einzusetzen')}
        maxLength={l.maxLength ?? undefined}
        {...feld.props}
      />
    </FeldZeile>
  )
}

function TexteKarte({ p, stelle, darf }: { p: CalcPosition; stelle: Speicherstelle<unknown>; darf: boolean }) {
  const gesperrt = !darf || p.isLvLocked
  const lang = useFeld(stelle, 'longText', { label: 'Langtext', gesperrt })
  const einleitung = useFeld(stelle, 'introText', { label: 'Einleitungstext', gesperrt })
  const art = useFeld<string>(stelle, 'kind', { art: 'auswahl', label: 'Art', gesperrt })
  const antwort = useFeld(stelle, 'gapAnswer', { label: 'Antwort der Bieterlücke', gesperrt: !darf })
  const regie = useFeld(stelle, 'regieQualificationCode', { label: 'Qualifikation (Regie)', gesperrt: !darf })
  const prozent = useFeld<number | null>(stelle, 'surchargePercent', { art: 'zahl', label: 'Zuschlag %', gesperrt: !darf })
  return (
    <Abschnitt titel="Texte und Art">
      <div className="space-y-2">
        <FeldZeile label="Art" status={art}>
          <select className={feldKlasse} {...art.props}>
            {ARTEN.map((a) => (
              <option key={a.wert} value={a.wert}>
                {a.text}
              </option>
            ))}
          </select>
        </FeldZeile>
        <FeldZeile label="Einleitungstext" status={einleitung}>
          <WachsendesFeld feld={einleitung.props} zeilen={2} />
        </FeldZeile>
        <FeldZeile label="Langtext" status={lang} hilfe="Steht im Angebot unter dem Kurztext.">
          <WachsendesFeld feld={lang.props} zeilen={6} />
        </FeldZeile>
        {p.kind === 'REGIE' && (
          <FeldZeile label="Qualifikation (Regie)" status={regie} hilfe="EP = Verrechnungssatz dieser Qualifikation (Kürzel).">
            <input className={feldKlasse} {...regie.props} />
          </FeldZeile>
        )}
        {p.kind === 'SURCHARGE' && (
          <FeldZeile label="Zuschlag %" status={prozent} hilfe={p.surchargeParentOz ? `auf die Positionen unter ${p.surchargeParentOz}` : 'auf alle zählenden Positionen'}>
            <input className={feldKlasse} inputMode="decimal" {...prozent.props} />
          </FeldZeile>
        )}
        {p.gaps && p.gaps.length > 0 ? (
          <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-3">
            <p className="text-xs text-muted-foreground">
              Lücken im Text — Pflichtlücken muss die Abgabe füllen, freiwillige nicht. Die Antwort steht in der Ausgabe an der Stelle der Lücke.
            </p>
            {p.gaps.map((l) => (
              <LueckenFeld key={l.seq} l={l} stelle={stelle} darf={darf} />
            ))}
          </div>
        ) : (
          p.gapQuestion && (
            <FeldZeile label="Bieterlücke" status={antwort} hilfe={p.gapQuestion}>
              <WachsendesFeld feld={antwort.props} zeilen={2} />
            </FeldZeile>
          )
        )}
      </div>
    </Abschnitt>
  )
}
